import { Box, TextField } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { sanitizeTestId } from '@roc/feature-utils';
import { useEffect, useState } from 'react';

const useStyles = makeStyles((theme) =>
  createStyles({
    autoComplete: {
      width: 300,
      [theme.breakpoints.up('xl')]: {
        width: 400,
      },
      '& input::placeholder': {
        opacity: 1,
      },
      '& input:focus::placeholder': {
        opacity: 0.5,
      },
    },
    standaloneLabel: {
      display: 'block',
      marginBottom: "4px",
    },
    popper: {
      minWidth: '300px',
    },
    listbox: {
      '& .MuiAutocomplete-listbox': {
        width: '80vw !important',
        maxWidth: '500px !important',
      },
    }
  })
);

interface AutocompleteSelectProps {
  label: string;
  options: Array<{ label: string; value: string | number }>;
  value: { label: string; value: string | number } | null;
  onChange: (value: { label: string; value: string | number } | null) => void;
  loading?: boolean;
  minWidth: string;
  defaultValue?: { label: string; value: string | number } | null;
}

const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({
  label,
  options,
  value,
  onChange,
  loading = false,
  minWidth = undefined,
  defaultValue = null
}) => {
  const classes = useStyles();
  const [selectedValue, setSelectedValue] = useState(defaultValue);

  useEffect(() => {
    setSelectedValue(value || defaultValue);
  }, []);

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: { label: string; value: string | number }) =>
      [option.label, option.value].join(' '),
  });

  const handleMenuItemClick = (event, value) => {
    setSelectedValue(value);
    onChange(value);
  };

  return (
    <Box>
      <label className={classes.standaloneLabel}>{label}</label>
      <Autocomplete
        size="small"
        style={{ minWidth: minWidth }}
        className={classes.autoComplete}
        id={`autocomplete-${sanitizeTestId(label)}`}
        options={options}
        value={selectedValue}
        onChange={handleMenuItemClick}
        getOptionLabel={(option) => option.label}
        getOptionSelected={(option, value) => option.value === value.value}
        filterOptions={filterOptions}
        disableClearable
        loading={loading}
        classes={{
          listbox: classes.listbox,
          popper: classes.popper,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            fullWidth
            size="small"
            InputProps={{
              ...params.InputProps,
            }}
            InputLabelProps={{
              shrink: false,
            }}
          />
        )}
      />
    </Box>
  );
};

export default AutocompleteSelect;
